import { Button, Form, Header, Icon, Table } from '@mairistem/composants';
import _ from 'lodash';
import React from 'react';

import type { PayeurType } from '@/types';
import { formatMonetaire, formatPayeurAdresseLignes, formatPayeurLabel } from '@/utils';

import { type LigneComptant, QUANTITE_MAX, QUANTITE_MIN, useQuantiteSaisie } from './comptant';

interface ComptantTableProps {
  // Récapitulatif figé : ni suppression du payeur ou d'une ligne, ni saisie de quantité.
  readOnly?: boolean;
  payeur: PayeurType | null;
  lignes: LigneComptant[];
  total: number;
  // Libellé de la dernière colonne (par défaut "Montant") : personnalisable pour les vues
  // en lecture seule (ex. reçu au comptant : "Montant encaissé").
  montantLabel?: string;
  // Libellé du footer (par défaut "Total à encaisser"), même logique que montantLabel.
  totalLabel?: string;
  onRemovePayeur?: () => void;
  onRemoveLigne?: (key: string) => void;
  onChangeQuantiteLigne?: (key: string, value: string) => void;
}

interface ComptantTableQuantiteProps {
  quantite: number;
  onChange: (value: string) => void;
}

// Champ de quantité d'une ligne : saisie libre (minimum imposé au blur, cf. useQuantiteSaisie)
// et boutons +/- qui committent immédiatement une variation de 1, bornée par clampQuantite.
const ComptantTableQuantite = ({ quantite, onChange }: ComptantTableQuantiteProps) => {
  const { raw, handleChange, handleBlur, handleStep } = useQuantiteSaisie(quantite, (value) =>
    onChange(String(value)),
  );

  const valeurCourante = raw === '' ? 0 : Number(raw);
  const auMinimum = valeurCourante <= QUANTITE_MIN;
  const auMaximum = valeurCourante >= QUANTITE_MAX;

  // Aux bornes, le bouton correspondant est masqué en entier (bordures ET icône) tout en
  // conservant son encombrement, pour ne décaler ni le champ ni l'autre bouton.
  // On évite volontairement `disabled` : le thème retire alors l'ombre-bordure des boutons et
  // force `opacity: .75 !important`, ce qui laisserait l'icône visible sans son cadre.
  const styleBoutonMasque = (masque: boolean) => ({
    margin: 0,
    ...(masque && { visibility: 'hidden' as const, opacity: 0, pointerEvents: 'none' as const }),
  });

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 6,
      }}
    >
      <Button
        type="button"
        icon="minus"
        data-testid="comptant-quantite-moins"
        aria-hidden={auMinimum || undefined}
        tabIndex={auMinimum ? -1 : undefined}
        onClick={() => handleStep(-1)}
        style={styleBoutonMasque(auMinimum)}
      />
      <Form.Field style={{ margin: 0 }}>
        <Form.Input
          type="number"
          min={QUANTITE_MIN}
          max={QUANTITE_MAX}
          value={raw}
          style={{ width: 50 }}
          input={{ style: { textAlign: 'center' } }}
          onChange={(e, { value }) => handleChange(value)}
          onFocus={(e) => e.target.select()}
          onBlur={handleBlur}
        />
      </Form.Field>
      <Button
        type="button"
        icon="plus"
        data-testid="comptant-quantite-plus"
        aria-hidden={auMaximum || undefined}
        tabIndex={auMaximum ? -1 : undefined}
        onClick={() => handleStep(1)}
        style={styleBoutonMasque(auMaximum)}
      />
    </div>
  );
};

const ComptantTable = (props: ComptantTableProps) => {
  const {
    readOnly = false,
    payeur,
    lignes,
    total,
    montantLabel = 'Montant',
    totalLabel = 'Total à encaisser',
    onRemovePayeur,
    onRemoveLigne,
    onChangeQuantiteLigne,
  } = props;

  // Survol par cellule : la croix ne s'affiche que sur la cellule réellement survolée.
  const [hoverPayeur, setHoverPayeur] = React.useState(false);
  const [hoverLigneKey, setHoverLigneKey] = React.useState<string | null>(null);

  const adresseLignes = React.useMemo(
    () => formatPayeurAdresseLignes(payeur ?? undefined),
    [payeur],
  );

  // Lignes affichées : les produits ajoutés, sinon une ligne vide si un payeur est sélectionné.
  const rows: (LigneComptant | null)[] = lignes.length > 0 ? lignes : payeur ? [null] : [];

  return (
    <Table celled striped structured fixed>
      <Table.Header>
        <Table.Row>
          <Table.HeaderCell width={5}>Payeur</Table.HeaderCell>
          <Table.HeaderCell width={5}>Produits</Table.HeaderCell>
          <Table.HeaderCell width={2} textAlign="center">
            Quantité
          </Table.HeaderCell>
          <Table.HeaderCell width={2} textAlign="right">
            Prix unitaire
          </Table.HeaderCell>
          <Table.HeaderCell width={2} textAlign="right">
            {montantLabel}
          </Table.HeaderCell>
        </Table.Row>
      </Table.Header>
      <Table.Body>
        {rows.map((ligne, index) => (
          <Table.Row key={ligne?.key ?? 'payeur-only'}>
            {index === 0 && (
              <Table.Cell
                rowSpan={rows.length}
                verticalAlign="middle"
                {...(!readOnly && {
                  className: 'comptant-cell-suppression',
                  onMouseEnter: () => setHoverPayeur(true),
                  onMouseLeave: () => setHoverPayeur(false),
                })}
              >
                <div>{formatPayeurLabel(payeur ?? undefined)}</div>
                {adresseLignes.map((l) => (
                  <div key={l}>{l}</div>
                ))}
                {!readOnly && !_.isNil(payeur) && (
                  <Icon
                    link
                    className="comptant-delete"
                    style={{ visibility: hoverPayeur ? 'visible' : 'hidden' }}
                    data-testid="comptant-delete-payeur"
                    name="close"
                    color="red"
                    onClick={onRemovePayeur}
                  />
                )}
              </Table.Cell>
            )}
            {ligne ? (
              <>
                <Table.Cell
                  {...(!readOnly && {
                    className: 'comptant-cell-suppression',
                    onMouseEnter: () => setHoverLigneKey(ligne.key),
                    onMouseLeave: () => setHoverLigneKey(null),
                  })}
                >
                  {ligne.produit.libelle}
                  {!readOnly && (
                    <Icon
                      link
                      className="comptant-delete"
                      style={{ visibility: hoverLigneKey === ligne.key ? 'visible' : 'hidden' }}
                      data-testid="comptant-delete-ligne"
                      name="close"
                      color="red"
                      onClick={() => onRemoveLigne?.(ligne.key)}
                    />
                  )}
                </Table.Cell>
                <Table.Cell textAlign="center">
                  {readOnly ? (
                    ligne.quantite
                  ) : (
                    <ComptantTableQuantite
                      quantite={ligne.quantite}
                      onChange={(value) => onChangeQuantiteLigne?.(ligne.key, value)}
                    />
                  )}
                </Table.Cell>
                <Table.Cell textAlign="right">
                  {formatMonetaire(ligne.produit.prixUnitaireTTC)}
                </Table.Cell>
                <Table.Cell textAlign="right">
                  {formatMonetaire(ligne.quantite * ligne.produit.prixUnitaireTTC)}
                </Table.Cell>
              </>
            ) : (
              <>
                <Table.Cell />
                <Table.Cell />
                <Table.Cell />
                <Table.Cell />
              </>
            )}
          </Table.Row>
        ))}
      </Table.Body>
      <Table.Footer>
        <Table.Row>
          <Table.HeaderCell colSpan={3} />
          <Table.HeaderCell textAlign="right" style={{ borderLeftColor: 'transparent' }}>
            <Header as="h4">{totalLabel}</Header>
          </Table.HeaderCell>
          <Table.HeaderCell
            textAlign="right"
            className="cycle-etat-en-cours"
            data-testid="comptant-total"
          >
            <Header as="h3">{formatMonetaire(total)}</Header>
          </Table.HeaderCell>
        </Table.Row>
      </Table.Footer>
    </Table>
  );
};

export default ComptantTable;
